import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconComponentType, InlineLink, StandAloneLink, Surface, Txt } from '@vst/beam';
import { OpenInNew, Phone } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { CustomStandardModal } from '@app/components/Modal';
import { AddonManagementModalType, openInNewTab, selectAddOns, setShowModal } from '@app/redux';

import { BenefitsAddOnModal } from './BenefitsAddOnModal';
import { CancelSubscriptionErrorModal } from './CancelSubscriptionErrorModal';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { findAddOn } from './utils';

import styles from './styles.module.scss';

type SupportOptionItemProps = {
  text: string;
  linkText: string;
  href: string;
  icon: IconComponentType;
  dataCy: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

const SupportOptionItem = ({ icon, text, linkText, onClick, href, dataCy }: SupportOptionItemProps) => (
  <Surface className={styles['support-option-item']} radius="16px" px={'24px'} py={'24px'}>
    <Txt variant={'bodyLargeRegular'} color={'regular'} component="span">
      {text}
    </Txt>
    <StandAloneLink
      variant="primary"
      href={href}
      onClick={onClick}
      icon={icon}
      style={{ textDecoration: 'none', fontWeight: '600' }}
      data-cy={dataCy}
    >
      {linkText}
    </StandAloneLink>
  </Surface>
);

type AddonManagementModalProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  addOnName: string;
  modalType: AddonManagementModalType | null;
  isBundleAddOn: boolean;
  isRescindable: boolean;
};

export const AddonManagementModal = ({
  id,
  isOpen,
  onClose,
  addOnName,
  modalType,
  isBundleAddOn,
  isRescindable,
}: AddonManagementModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Profile');
  const { currentAddOns } = useSelector(selectAddOns);

  const [modal, setModal] = useState<AddonManagementModalType | null>(null);
  const closeModal = () => setModal(null);
  const isCancelSubscriptionErrorModal = modalType === AddonManagementModalType.CancelSubscriptionError;

  const addOn = findAddOn(currentAddOns, addOnName);

  if (!addOn) {
    return null;
  }

  const hasValidAttributes = addOn?.attributes?.some((benefit) => benefit?.value) ?? false;
  const kind = addOn.kind;

  const handleHelpCenterClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const url = t('visitHelpCenter.url');
    dispatch(openInNewTab(url));
  };

  const handleCancelSubscription = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (hasValidAttributes) {
      setModal(AddonManagementModalType.Benefits);
    } else {
      setModal(AddonManagementModalType.Subscription);
    }
    onClose();
  };

  const handleBenefitsModal = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setModal(AddonManagementModalType.Subscription);
    onClose();
  };

  if (isCancelSubscriptionErrorModal) {
    return (
      <CancelSubscriptionErrorModal
        isOpen={isCancelSubscriptionErrorModal}
        onClose={() => {
          dispatch(setShowModal(null));
        }}
        addOnName={addOnName}
      />
    );
  }
  return (
    <>
      <CustomStandardModal
        isOpen={isOpen}
        header={t('manageAddonModal.header', { addOnName })}
        actions={{
          primary: { label: t('manageAddonModal.label'), onClick: onClose },
        }}
        onClose={onClose}
        size="medium"
      >
        <div>
          <Txt variant={'bodyLargeRegular'} color={'regular'}>
            {t('manageAddonModal.description', { addOnName })}
          </Txt>
          <Surface mb={'24px'} className={styles['support-options-container']}>
            <SupportOptionItem
              icon={OpenInNew}
              text={t('visitHelpCenter.text')}
              linkText={t('visitHelpCenter.link')}
              onClick={handleHelpCenterClick}
              dataCy="visit-help-center"
              href={'#'}
            />
            <SupportOptionItem
              icon={Phone}
              text={t('callCustomerCare.text')}
              linkText={t('callCustomerCare.link')}
              href={`tel:${t('Global:callCustomerSupportPhoneNumber')}`}
              dataCy="call-customer-care"
            />
          </Surface>
          {!isBundleAddOn && (
            <Txt variant={'bodySmallRegular'} color={'regular'} component="span">
              {t('cancelSubscription.title')}
              <InlineLink
                data-cy="manage-addon-link"
                variant="primary"
                href="#"
                onClick={handleCancelSubscription}
                style={{ textDecoration: 'none' }}
                ml={'4px'}
              >
                {t('cancelSubscription.link')}
              </InlineLink>
            </Txt>
          )}
        </div>
      </CustomStandardModal>

      {modal === AddonManagementModalType.Benefits && (
        <BenefitsAddOnModal
          isOpen={modal === AddonManagementModalType.Benefits}
          onClose={closeModal}
          handleClick={closeModal}
          removeAddOn={handleBenefitsModal}
          addOnName={addOnName}
        />
      )}
      {modal === AddonManagementModalType.Subscription && (
        <CancelSubscriptionModal
          id={id}
          addOnKind={kind}
          isOpen={modal === AddonManagementModalType.Subscription}
          onClose={closeModal}
          addOnName={addOnName}
          isRescindable={isRescindable}
        />
      )}
    </>
  );
};
